import { Injectable, OnDestroy } from '@angular/core';
import { 
  Storage, 
  getDownloadURL,
  ref, 
  uploadBytesResumable,
  UploadTaskSnapshot
} from '@angular/fire/storage';
import { inject } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class FirebaseStorageService implements OnDestroy {
  private storage: Storage;
  private activeUploads = new Map<string, any>();

  constructor() {
    // Use Angular's inject function to get the Storage service
    this.storage = inject(Storage);
    
    // Verify Firebase is initialized
    if (!this.storage) {
      console.error(
        'Firebase Storage not available. Make sure you have initialized Firebase correctly: ' +
        'provideFirebaseApp(() => initializeApp(config)) and provideStorage(() => getStorage()) ' +
        'must be included in your app providers.'
      );
    }
  }

  ngOnDestroy() {
    // Clean up any active uploads when service is destroyed
    this.activeUploads.forEach(uploadTask => {
      try {
        if (uploadTask && uploadTask.cancel) {
          uploadTask.cancel();
        }
      } catch (e) {
        console.error('Error cancelling upload:', e);
      }
    });
    this.activeUploads.clear();
  }

  async uploadFile(
    storagePathPrefix: string, 
    file: File, 
    onProgress?: (percent: number) => void
  ): Promise<string> {
    // Validate inputs
    if (!file) {
      throw new Error('No file provided for upload');
    }

    if (!this.storage) {
      throw new Error(
        'Firebase Storage not initialized. Make sure provideStorage() is included in your app providers.'
      );
    }

    try {
      // Create a unique file path
      const timestamp = Date.now();
      const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fullPath = `${storagePathPrefix}/${timestamp}_${safeName}`;
      const uploadId = `${fullPath}_${timestamp}`;
      
      // Create the storage reference
      const storageRef = ref(this.storage, fullPath);
      
      // Start the upload
      const uploadTask = uploadBytesResumable(storageRef, file);
      this.activeUploads.set(uploadId, uploadTask);

      // Return a promise that resolves with the download URL when complete
      return new Promise<string>((resolve, reject) => {
        uploadTask.on(
          'state_changed',
          (snapshot: UploadTaskSnapshot) => {
            const percent = Math.round(
              (snapshot.bytesTransferred / snapshot.totalBytes) * 100
            );
            if (onProgress) {
              onProgress(percent);
            }
          },
          (error) => {
            this.activeUploads.delete(uploadId);
            reject(error);
          },
          async () => {
            try {
              const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
              this.activeUploads.delete(uploadId);
              resolve(downloadURL);
            } catch (error) {
              this.activeUploads.delete(uploadId);
              reject(error);
            }
          }
        );
      });
    } catch (error) {
      console.error('Upload failed:', error);
      throw error;
    }
  }
}